import { useCallback, useRef } from 'react';
import { ApolloError } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  useErpLinkKommoLeadMutation,
  useErpResetKommoLeadLinkMutation,
} from 'core/graphql';
import { ToastTypes } from 'types';

import { useLeadErrorText, useLeadsNotify } from './leadsFeedback';
import { LeadLinkTarget } from './leadsModel';

/**
 * ТЗ 0086 — ручная связь лида с заявкой дофинансирования или студентом.
 *
 * `link(target, id)` — связать с выбранным, `link(target, null)` — отвязать;
 * обе запирают связь (робот её больше не трогает). `reset(target)` —
 * вернуть роботу: замок снимается, связь пересверяется. Ответ — лид целиком,
 * та же запись кэша `KommoLeadType:<id>`: карточка и строка списка
 * обновляются без перечитывания.
 *
 * Отказы — тостом с переводом кода (`leads.errors.<CODE>`); повторное
 * нажатие, пока первое в полёте, ничего не шлёт.
 */
export const useLeadLinks = (leadId: string) => {
  const { t } = useTranslation();
  const notify = useLeadsNotify();
  const errorText = useLeadErrorText();
  const [linkMut, linkState] = useErpLinkKommoLeadMutation();
  const [resetMut, resetState] = useErpResetKommoLeadLinkMutation();
  const inFlight = useRef(false);

  const settle = useCallback(
    (
      payload:
        | {
            success?: boolean | null;
            customErrors?: Array<{
              code?: string | null;
              message?: string | null;
            } | null> | null;
          }
        | null
        | undefined,
      successKey: string,
    ): boolean => {
      const errors = (payload?.customErrors ?? []).filter(Boolean);
      if (!payload?.success || errors.length) {
        notify(
          ToastTypes.error,
          t('leads.links.toast.failed'),
          errors.map((error) => errorText(error)).join('\n') || undefined,
        );
        return false;
      }
      notify(ToastTypes.success, t(successKey));
      return true;
    },
    [errorText, notify, t],
  );

  const fail = useCallback(
    (caught: unknown): boolean => {
      notify(
        ToastTypes.error,
        t('leads.links.toast.failed'),
        errorText({
          code:
            caught instanceof ApolloError && caught.graphQLErrors.length > 0
              ? 'NO_ACCESS'
              : 'NETWORK',
          message: '',
        }),
      );
      return false;
    },
    [errorText, notify, t],
  );

  const link = useCallback(
    async (what: LeadLinkTarget, targetId: string | null): Promise<boolean> => {
      if (inFlight.current) return false;
      inFlight.current = true;
      try {
        const { data } = await linkMut({
          variables: { leadId, what, targetId },
        });
        return settle(
          data?.linkKommoLead,
          targetId
            ? `leads.links.toast.linked.${what}`
            : `leads.links.toast.unlinked.${what}`,
        );
      } catch (caught) {
        return fail(caught);
      } finally {
        inFlight.current = false;
      }
    },
    [fail, leadId, linkMut, settle],
  );

  const reset = useCallback(
    async (what: LeadLinkTarget): Promise<boolean> => {
      if (inFlight.current) return false;
      inFlight.current = true;
      try {
        const { data } = await resetMut({ variables: { leadId, what } });
        return settle(data?.resetKommoLeadLink, 'leads.links.toast.reset');
      } catch (caught) {
        return fail(caught);
      } finally {
        inFlight.current = false;
      }
    },
    [fail, leadId, resetMut, settle],
  );

  return { link, reset, busy: linkState.loading || resetState.loading };
};
